import { useState, useRef, useEffect, useLayoutEffect, useId, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styles from "./SearchableSelect.module.css";

export interface SearchableSelectOption {
	value: string;
	label: string;
	meta?: Record<string, string>;
}

interface SearchableSelectProps {
	value?: string;
	onChange: (value: string, option?: SearchableSelectOption) => void;
	options?: SearchableSelectOption[];
	loadOptions?: (query: string) => Promise<SearchableSelectOption[]>;
	initialOption?: SearchableSelectOption | undefined;
	refetchKey?: string | number | undefined;
	placeholder?: string;
	loading?: boolean;
	disabled?: boolean | undefined;
	clearable?: boolean;
	label?: string;
	uppercase?: boolean | undefined;
}

export function SearchableSelect({
	value,
	onChange,
	options: staticOptions,
	loadOptions,
	initialOption,
	refetchKey,
	placeholder,
	loading: externalLoading,
	disabled,
	clearable,
	label,
	uppercase,
}: SearchableSelectProps) {
	const { t, i18n } = useTranslation();
	const id = useId();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [items, setItems] = useState<SearchableSelectOption[]>(staticOptions ?? []);
	const [loading, setLoading] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [dropdownPos, setDropdownPos] = useState<{
		top: number;
		left: number;
		width: number;
	} | null>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const loadOptionsRef = useRef(loadOptions);

	useEffect(() => {
		loadOptionsRef.current = loadOptions;
	}, [loadOptions]);

	const [selectedOption, setSelectedOption] = useState<SearchableSelectOption | undefined>(
		initialOption,
	);

	const filteredStaticOptions = useMemo(() => {
		if (loadOptions || !staticOptions) return null;
		const q = query.toLocaleLowerCase("tr");
		return q
			? staticOptions.filter((o) => o.label.toLocaleLowerCase("tr").includes(q))
			: staticOptions;
	}, [staticOptions, query, loadOptions]);

	const displayItems = useMemo(
		() => (loadOptions ? items : (filteredStaticOptions ?? [])),
		[loadOptions, items, filteredStaticOptions],
	);

	const selectedLabel =
		(staticOptions ?? items).find((o) => o.value === value)?.label ??
		(value && selectedOption?.value === value ? selectedOption.label : undefined) ??
		(value ? initialOption?.label : undefined) ??
		"";

	useEffect(() => {
		if (!loadOptionsRef.current) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			const lo = loadOptionsRef.current;
			if (!lo) return;
			const q = query;
			setLoading(true);
			lo(q)
				.then((result) => setItems(result))
				.catch(() => setItems([]))
				.finally(() => setLoading(false));
			setHighlightedIndex(0);
		}, 300);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [query, refetchKey]);

	useEffect(() => {
		function onClickOutside(e: MouseEvent) {
			const target = e.target as Node;
			if (containerRef.current?.contains(target)) return;
			if (dropdownRef.current?.contains(target)) return;
			setOpen(false);
		}
		if (open) {
			document.addEventListener("mousedown", onClickOutside);
		}
		return () => document.removeEventListener("mousedown", onClickOutside);
	}, [open]);

	useEffect(() => {
		if (!open) return;
		function updatePosition() {
			const el = containerRef.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
		}
		updatePosition();
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);
		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [open]);

	// Dropdown içerik genişliğine göre (uzun ülke isimleri gibi) tetikleyiciden geniş
	// açılabilir; gerçek render genişliği ölçülüp ekranın sağ kenarını taşıyorsa sola kaydırılır.
	useLayoutEffect(() => {
		if (!open || !dropdownPos) return;
		const width = dropdownRef.current?.getBoundingClientRect().width;
		if (!width) return;
		const margin = 8;
		const maxLeft = Math.max(margin, window.innerWidth - width - margin);
		if (dropdownPos.left > maxLeft) {
			setDropdownPos((prev) => (prev ? { ...prev, left: maxLeft } : prev));
		}
	}, [open, dropdownPos, displayItems]);

	const handleSelect = (option: SearchableSelectOption) => {
		setSelectedOption(option);
		onChange(option.value, option);
		setQuery("");
		setOpen(false);
	};

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		setSelectedOption(undefined);
		onChange("");
		setQuery("");
		inputRef.current?.focus();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!open) {
			if (e.key === "ArrowDown" || e.key === "Enter") {
				setOpen(true);
			}
			return;
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightedIndex((i) => Math.min(i + 1, displayItems.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightedIndex((i) => Math.max(i - 1, 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			const opt = displayItems[highlightedIndex];
			if (opt) handleSelect(opt);
		} else if (e.key === "Escape") {
			setOpen(false);
		}
	};

	const displayValue = open ? query : selectedLabel;

	return (
		<div className={styles.wrapper} ref={containerRef}>
			{label && (
				<label htmlFor={id} className={styles.label}>
					{label}
				</label>
			)}
			<div
				className={[styles.inputWrap, open && styles.open, disabled && styles.disabled]
					.filter(Boolean)
					.join(" ")}
			>
				<input
					id={id}
					ref={inputRef}
					type="text"
					className={styles.input}
					value={displayValue}
					placeholder={placeholder}
					disabled={disabled}
					onChange={(e) => {
						setQuery(
							uppercase
								? e.target.value.toLocaleUpperCase(i18n.language.slice(0, 2))
								: e.target.value,
						);
						if (!open) setOpen(true);
					}}
					onFocus={() => {
						setOpen(true);
						if (loadOptions && items.length === 0) {
							setQuery("");
						}
					}}
					onKeyDown={handleKeyDown}
					autoComplete="off"
				/>
				{clearable && value && (
					<button type="button" className={styles.clear} onClick={handleClear} tabIndex={-1}>
						<svg
							width="14"
							height="14"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M18 6 6 18" />
							<path d="m6 6 12 12" />
						</svg>
					</button>
				)}
				<span className={styles.chevron}>
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="m6 9 6 6 6-6" />
					</svg>
				</span>
			</div>
			{open &&
				dropdownPos &&
				createPortal(
					<div
						ref={dropdownRef}
						className={styles.dropdown}
						style={{
							position: "fixed",
							top: dropdownPos.top,
							left: dropdownPos.left,
							minWidth: dropdownPos.width,
							zIndex: 200,
						}}
					>
						{(loading || externalLoading) && (
							<div className={styles.empty}>{t("loadingOptions")}</div>
						)}
						{!loading && !externalLoading && displayItems.length === 0 && (
							<div className={styles.empty}>{t("noResults")}</div>
						)}
						{!loading &&
							!externalLoading &&
							displayItems.map((item, idx) => (
								<div
									key={item.value}
									className={[
										styles.item,
										idx === highlightedIndex && styles.highlighted,
										item.value === value && styles.selected,
									]
										.filter(Boolean)
										.join(" ")}
									onMouseEnter={() => setHighlightedIndex(idx)}
									onClick={() => handleSelect(item)}
								>
									{item.label}
								</div>
							))}
					</div>,
					document.body,
				)}
		</div>
	);
}
